import {useMergedConnection} from '@github-ui/optimistic-connection/useMergedConnection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'

import {LABELS} from './constants/labels'
import {MilestoneError} from './MilestoneError'
import {MilestoneList} from './MilestoneList'
import {MilestonesActions} from './MilestonesActions'
import type {RepositoryResult} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'
import {MILESTONES_CONNECTION_PATH} from './utils/milestone-connection-operations'

// RELAY-CLEANUP(RepositoryMilestoneIndexPageContentInternal): widens nullable fields so the still-Relay parent
// data is assignable. Remove when RepositoryMilestoneIndexPageContentInternal leaves Relay.
export type RepositoryMilestonesInternalFragment = RelayCompat<
  Pick<RepositoryResult, 'id' | 'viewerCanPush' | 'nameWithOwner' | 'milestones' | 'open' | 'closed'>
>

type RepositoryMilestonesInternalProps = {
  repository: RepositoryMilestonesInternalFragment
  // Present only in the data-router app; enables the optimistic overlay + settle fold on the route query.
  queryKey?: RouteQueryKey
}

export function RepositoryMilestonesInternal({repository: data, queryKey}: RepositoryMilestonesInternalProps) {
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  // Overlay in-flight close/reopen ops onto the query data so the list and counts update instantly
  // (data-router app only; the Relay app manages its own store).
  //
  // Known limitation: ops are scoped by repo + connection path, not by tab/page/sort variant, so a close
  // still in flight when the user switches variant briefly leaks into the destination until it settles —
  // narrow and self-correcting. Tracked in github/github-ui#27757.
  const repository = useMergedConnection({
    base: data,
    ownerId: data.id,
    connectionPath: MILESTONES_CONNECTION_PATH,
    enabled: queryKey != null,
  })

  return (
    <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}>
      <ErrorBoundary
        fallback={<MilestoneError title={LABELS.milestonesError} message={LABELS.milestonesErrorMessage} />}
      >
        <MilestonesActions repository={repository} />
        <MilestoneList repository={repository} queryKey={queryKey} />
      </ErrorBoundary>
    </div>
  )
}
